import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { type AppProps, defaultRuntimeCommunity, defaultRuntimeUser, type RuntimeUser } from '../common/interfaces';

export const defaultRuntime: AppProps = {
  apiV2Url: '',
  sessionId: '',
  sessionTimeout: '',
  cloudinaryUrl: '',
  mirp: { enabled: false, region: '', url: '' },
  passwordComplexityEnabled: 'false',
  passwordComplexityLengthMin: '1',
  passwordComplexitySpecialMin: '1',
  passwordComplexitySpecialChars: '',
  passwordComplexityLengthUpperMin: '1',
  passwordComplexityLengthLowerMin: '1',
  passwordComplexityLengthNumberMin: '1',
  passwordComplexityLifetimeMinutesMin: '0',
  community: defaultRuntimeCommunity,
  user: defaultRuntimeUser,
  regionDbCode: 'dev',
  popularTags: [],
  device: 'desktop',
  isPeopleSearchEnabled: true,
  maxUploadBytes: 0
};

const RuntimeContext = createContext<AppProps>(defaultRuntime);

const RuntimeProvider = ({ children, value }: { readonly children: ReactNode; readonly value: AppProps }) => {
  const {
    apiV2Url,
    cloudinaryUrl,
    sessionId,
    sessionTimeout,
    mirp: { enabled, url, region },
    passwordComplexityEnabled,
    passwordComplexityLengthMin,
    passwordComplexitySpecialMin,
    passwordComplexitySpecialChars,
    passwordComplexityLengthUpperMin,
    passwordComplexityLengthLowerMin,
    passwordComplexityLengthNumberMin,
    passwordComplexityLifetimeMinutesMin,
    user: initialUser,
    community,
    regionDbCode,
    popularTags,
    device,
    isPeopleSearchEnabled,
    maxUploadBytes
  } = value;
  const [user, setUser] = useState<RuntimeUser | undefined>(initialUser);
  const memoizedValue = useMemo(
    () => ({
      apiV2Url,
      cloudinaryUrl,
      mirp: { enabled, url, region },
      sessionId,
      sessionTimeout,
      passwordComplexityEnabled,
      passwordComplexityLengthMin,
      passwordComplexitySpecialMin,
      passwordComplexitySpecialChars,
      passwordComplexityLengthUpperMin,
      passwordComplexityLengthLowerMin,
      passwordComplexityLengthNumberMin,
      passwordComplexityLifetimeMinutesMin,
      user,
      community,
      regionDbCode,
      popularTags,
      device,
      isPeopleSearchEnabled,
      maxUploadBytes
    }),
    [
      apiV2Url,
      cloudinaryUrl,
      enabled,
      url,
      region,
      sessionId,
      sessionTimeout,
      passwordComplexityEnabled,
      passwordComplexityLengthMin,
      passwordComplexitySpecialMin,
      passwordComplexitySpecialChars,
      passwordComplexityLengthUpperMin,
      passwordComplexityLengthLowerMin,
      passwordComplexityLengthNumberMin,
      passwordComplexityLifetimeMinutesMin,
      user,
      community,
      regionDbCode,
      popularTags,
      device,
      isPeopleSearchEnabled,
      maxUploadBytes
    ]
  );

  const updateUserContextHandler = useCallback(
    (event: CustomEvent | Event): void => {
      if ('detail' in event) {
        const { avatar }: { avatar: string } = event.detail;
        const newUser = { ...user, avatar };
        setUser(newUser as RuntimeUser);
      }
    },
    [user]
  );

  useEffect(() => {
    window.addEventListener('updateUserContext', updateUserContextHandler);

    return () => {
      window.removeEventListener('updateUserContext', updateUserContextHandler);
    };
  }, [updateUserContextHandler]);

  return <RuntimeContext.Provider value={memoizedValue}>{children}</RuntimeContext.Provider>;
};

const RuntimeConsumer = RuntimeContext.Consumer;

const useRuntimeConfig = () => useContext(RuntimeContext);

export { RuntimeContext, RuntimeConsumer, RuntimeProvider, useRuntimeConfig };
